import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertCircle, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { cn } from '@/lib/utils'
import { useQueryClient } from '@tanstack/react-query'
import {
  formatCad,
  useApplyChangePlan,
  useCreateSubscriptionInline,
  usePreviewChangePlan,
  type PreviewChangePlanResponse,
} from '@/api/billing'
import { useUIStore } from '@/stores/useUIStore'
import type { BillingPeriod, PlanDescriptor } from './plans'
import { UpdateCardDialog } from './UpdateCardDialog'

interface Props {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Plan choisi par l'utilisateur dans ChangePlanView. */
  targetPlan: PlanDescriptor | null
  /** Période de facturation choisie. */
  billing: BillingPeriod
  /** True si l'utilisateur est actuellement sur un plan legacy/grandfathered. */
  isLegacyMigration: boolean
}

/**
 * Dialog de confirmation upgrade / downgrade / migration legacy.
 *
 * Récupère le preview Stripe au mount (POST /api/billing/preview-change/) qui
 * retourne les lignes de proration + total à prélever immédiatement.
 *
 * Cas upgrade (amount_due > 0) : affiche les lignes + total TTC + bouton
 * "Confirmer et payer X CAD".
 * Cas downgrade (amount_due == 0) : affiche juste "Aucun prélèvement immédiat"
 * + récap du nouveau récurrent.
 *
 * Backend endpoint POST /api/stripe/change-plan/ pour exécuter le changement
 * sera implémenté dans un commit suivant.
 */
export function ConfirmPlanChangeDialog({
  open,
  onOpenChange,
  targetPlan,
  billing,
  isLegacyMigration,
}: Props) {
  const { t, i18n } = useTranslation()
  const darkMode = useUIStore((s) => s.darkMode)
  const queryClient = useQueryClient()
  const previewMutation = usePreviewChangePlan()
  const createSubMutation = useCreateSubscriptionInline()
  const applyMutation = useApplyChangePlan()
  /**
   * État pour le fallback in-app : quand un paiement échoue (CB refusée,
   * SCA, etc.), on ouvre UpdateCardDialog au lieu de basculer Checkout.
   * Stocke la dernière action à retry après MAJ CB :
   *   - 'create' = retry createSubMutation (cas sub canceled)
   *   - 'apply'  = retry applyMutation (cas change plan sur sub active)
   */
  const [pendingRetry, setPendingRetry] = useState<null | 'create' | 'apply'>(null)

  // Trigger preview à l'ouverture (et reset au close)
  useEffect(() => {
    if (open && targetPlan && !targetPlan.isEnterprise) {
      previewMutation.mutate({
        target_plan: targetPlan.key,
        billing_period: billing,
      })
    }
    if (!open) {
      previewMutation.reset()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, targetPlan?.key, billing])

  if (!targetPlan) return null

  const preview = previewMutation.data
  const previewLoading = previewMutation.isPending
  const previewError = previewMutation.isError
  const requiresReactivation = preview?.requires_reactivation === true
  const requiresNewSubscription = preview?.requires_new_subscription === true

  const targetName = t(
    `manager.billing.plans.${targetPlan.key}.name`,
    targetPlan.key,
  )

  // Helper : tente create_subscription_inline et gère les retours.
  // Si la CB échoue (requires_checkout), on ouvre UpdateCardDialog inline
  // au lieu de rediriger Stripe Checkout, et on retry après MAJ CB.
  const tryCreateSubscription = async () => {
    try {
      const res = await createSubMutation.mutateAsync({
        target_plan: targetPlan.key,
        billing_period: billing,
      })

      if (res.requires_checkout) {
        // Au lieu de Stripe Checkout : ouvre UpdateCardDialog inline.
        // Après MAJ CB réussie, on retry automatiquement (cf onSuccess du
        // dialog dans le JSX plus bas).
        setPendingRetry('create')
        return
      }

      toast.success(
        res.amount_paid_cad && Number(res.amount_paid_cad) > 0
          ? t(
              'manager.billing.changePlan.subscribeSuccessPaid',
              'Abonnement {{plan}} activé. {{amount}} prélevés.',
              {
                plan: res.plan_name ?? targetName,
                amount: formatCad(res.amount_paid_cad),
              },
            )
          : t(
              'manager.billing.changePlan.subscribeSuccess',
              'Abonnement {{plan}} activé.',
              { plan: res.plan_name ?? targetName },
            ),
      )
      queryClient.invalidateQueries({ queryKey: ['billing-summary'] })
      onOpenChange(false)
    } catch (err) {
      // Le compte a déjà un abonnement vivant côté Stripe : on recharge la
      // facturation, qui proposera alors de changer de plan.
      if ((err as { response?: { data?: { code?: string } } })?.response?.data?.code === 'subscription_exists') {
        queryClient.invalidateQueries({ queryKey: ['billing-summary'] })
        onOpenChange(false)
        toast.error(t('checkout.subscriptionExists.title', 'Vous avez déjà un abonnement'))
        return
      }
      toast.error(
        t(
          'manager.billing.changePlan.checkoutError',
          "Impossible d'activer l'abonnement. Réessaye dans un instant.",
        ),
      )
    }
  }

  // Helper : tente apply-change-plan (sub active existante). Si la CB échoue
  // sur la proration immédiate, ouvre UpdateCardDialog puis retry.
  const tryApplyChange = async () => {
    try {
      const res = await applyMutation.mutateAsync({
        target_plan: targetPlan.key,
        billing_period: billing,
        proration_date: preview?.proration_date,
      })

      if (res.requires_payment_update) {
        setPendingRetry('apply')
        return
      }

      if (res.no_change) {
        toast.info(t('manager.billing.changePlan.noChange', 'Plan inchangé'))
        onOpenChange(false)
        return
      }

      toast.success(
        res.effect === 'next_cycle'
          ? t(
              'manager.billing.changePlan.downgradeApplied',
              'Plan {{plan}} actif au prochain renouvellement.',
              { plan: res.plan_name ?? targetName },
            )
          : res.amount_paid_cad && Number(res.amount_paid_cad) > 0
            ? t(
                'manager.billing.changePlan.upgradeApplied',
                'Plan {{plan}} activé. {{amount}} prélevés.',
                {
                  plan: res.plan_name ?? targetName,
                  amount: formatCad(res.amount_paid_cad),
                },
              )
            : t(
                'manager.billing.changePlan.changeApplied',
                'Plan {{plan}} activé.',
                { plan: res.plan_name ?? targetName },
              ),
      )
      queryClient.invalidateQueries({ queryKey: ['billing-summary'] })
      onOpenChange(false)
    } catch {
      toast.error(
        t(
          'manager.billing.changePlan.applyError',
          "Impossible d'appliquer le changement. Réessaye dans un instant.",
        ),
      )
    }
  }

  const handleConfirm = async () => {
    if (requiresNewSubscription) {
      await tryCreateSubscription()
      return
    }
    await tryApplyChange()
  }

  // Cas Enterprise → message simple (pas de preview Stripe)
  if (targetPlan.isEnterprise) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('manager.billing.changePlan.titleEnterprise', 'Plan Enterprise')}
            </DialogTitle>
            <DialogDescription>
              {t(
                'manager.billing.changePlan.enterpriseDesc',
                "Pour Enterprise, notre équipe vous contactera pour la mise en place.",
              )}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t('common.cancel', 'Annuler')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>
            {t('manager.billing.changePlan.title', 'Changer vers {{plan}}', {
              plan: targetName,
            })}
          </DialogTitle>
          <DialogDescription>
            {previewLoading
              ? t(
                  'manager.billing.changePlan.previewLoading',
                  'Calcul du montant en cours…',
                )
              : previewError
                ? t(
                    'manager.billing.changePlan.previewError',
                    'Impossible de calculer le détail. Vous pouvez quand même confirmer.',
                  )
                : preview?.en_essai
                  ? t(
                      'manager.billing.changePlan.trialDesc',
                      'Vous passez à {{plan}} tout de suite. Rien n’est prélevé pendant l’essai : votre première facture, le {{date}}, sera de {{amount}}.',
                      {
                        plan: targetName,
                        date: dateFacture(preview.premiere_facture_le, i18n.language),
                        amount: formatCad(preview.recurring?.amount_cad),
                      },
                    )
                : preview?.is_upgrade
                  ? t(
                      'manager.billing.changePlan.upgradeDesc',
                      'Vous allez passer à {{plan}}. Prélèvement immédiat au prorata.',
                      { plan: targetName },
                    )
                  : t(
                      'manager.billing.changePlan.downgradeDesc',
                      "Vous passerez à {{plan}} au renouvellement. D'ici là, vous gardez toutes les fonctions de votre plan actuel.",
                      { plan: targetName },
                    )}
          </DialogDescription>
        </DialogHeader>

        {/* Preview loader */}
        {previewLoading && (
          <div className="flex justify-center py-6">
            <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
          </div>
        )}

        {/* Preview error */}
        {previewError && (
          <div
            className={cn(
              'rounded-lg border p-3 flex items-start gap-2',
              darkMode
                ? 'border-amber-500/30 bg-amber-500/10 text-amber-200'
                : 'border-amber-200 bg-amber-50 text-amber-900',
            )}
          >
            <AlertCircle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <p className="text-xs">
              {previewMutation.error instanceof Error
                ? previewMutation.error.message
                : t(
                    'manager.billing.changePlan.previewErrorDetail',
                    'Le détail des montants ne peut pas être affiché pour le moment.',
                  )}
            </p>
          </div>
        )}

        {/* Cas spécial : subscription annulée → demande réactivation préalable */}
        {requiresReactivation && (
          <div
            className={cn(
              'rounded-lg border p-4 flex items-start gap-3',
              darkMode
                ? 'border-amber-500/30 bg-amber-500/10'
                : 'border-amber-200 bg-amber-50',
            )}
          >
            <AlertCircle
              className={cn(
                'h-5 w-5 flex-shrink-0 mt-0.5',
                darkMode ? 'text-amber-400' : 'text-amber-600',
              )}
            />
            <div className="flex-1 space-y-1">
              <p
                className={cn(
                  'text-sm font-medium',
                  darkMode ? 'text-amber-200' : 'text-amber-900',
                )}
              >
                {t(
                  'manager.billing.changePlan.requiresReactivationTitle',
                  'Réactivez votre abonnement actuel d\'abord',
                )}
              </p>
              <p
                className={cn(
                  'text-xs',
                  darkMode ? 'text-amber-300/80' : 'text-amber-800/80',
                )}
              >
                {t(
                  'manager.billing.changePlan.requiresReactivationDesc',
                  'Votre abonnement est planifié pour annulation. Réactivez-le depuis le récap pour pouvoir choisir un nouveau plan.',
                )}
              </p>
            </div>
          </div>
        )}

        {/* Cas spécial : pas d'abonnement actif ou sub vraiment canceled
            → on propose direct un nouveau checkout au lieu d'un switch. */}
        {requiresNewSubscription && (
          <div
            className={cn(
              'rounded-lg border p-4 flex items-start gap-3',
              darkMode
                ? 'border-blue-500/30 bg-blue-500/10'
                : 'border-blue-200 bg-blue-50',
            )}
          >
            <AlertCircle
              className={cn(
                'h-5 w-5 flex-shrink-0 mt-0.5',
                darkMode ? 'text-blue-400' : 'text-blue-600',
              )}
            />
            <div className="flex-1 space-y-1">
              <p
                className={cn(
                  'text-sm font-medium',
                  darkMode ? 'text-blue-200' : 'text-blue-900',
                )}
              >
                {t(
                  'manager.billing.changePlan.requiresNewSubTitle',
                  'Souscris à un nouveau plan',
                )}
              </p>
              <p
                className={cn(
                  'text-xs',
                  darkMode ? 'text-blue-300/80' : 'text-blue-800/80',
                )}
              >
                {t(
                  'manager.billing.changePlan.requiresNewSubDesc',
                  "Votre ancien abonnement a expiré. Le paiement sera effectué sur votre carte enregistrée. Si elle n'est plus valide, vous pourrez la mettre à jour ici.",
                )}
              </p>
            </div>
          </div>
        )}

        {/* Preview content normal */}
        {preview &&
          !previewLoading &&
          !requiresReactivation &&
          !requiresNewSubscription && (
            <PreviewBlock preview={preview} darkMode={darkMode} />
          )}

        <DialogFooter className="flex-col sm:flex-row gap-2 items-stretch sm:items-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="sm:order-1 order-2"
          >
            {t('common.cancel', 'Annuler')}
          </Button>
          <Button
            type="button"
            onClick={handleConfirm}
            disabled={
              previewLoading ||
              requiresReactivation ||
              createSubMutation.isPending ||
              applyMutation.isPending
            }
            className="sm:order-2 order-1"
          >
            {(createSubMutation.isPending || applyMutation.isPending) && (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            )}
            {requiresReactivation
              ? t('manager.billing.changePlan.cannotChange', 'Indisponible')
              : requiresNewSubscription
                ? t(
                    'manager.billing.changePlan.subscribeTo',
                    'Souscrire à {{plan}}',
                    { plan: targetName },
                  )
                : preview?.is_upgrade &&
                    Number(preview?.total_immediate_cad ?? '0') > 0
                  ? t(
                      'manager.billing.changePlan.confirmAndPay',
                      'Confirmer et payer {{amount}}',
                      { amount: formatCad(preview?.total_immediate_cad) },
                    )
                  : t(
                      'manager.billing.changePlan.confirm',
                      'Confirmer le changement',
                    )}
          </Button>
        </DialogFooter>

        {isLegacyMigration && (
          <p
            className={cn(
              'text-xs mt-2 text-right',
              darkMode ? 'text-slate-500' : 'text-slate-400',
            )}
          >
            {t(
              'manager.billing.changePlan.legacyWarning',
              'Vous perdrez votre ancien tarif et ne pourrez plus y revenir.',
            )}
          </p>
        )}
      </DialogContent>

      {/* Fallback in-app : MAJ CB puis retry l'action qui avait échoué. */}
      <UpdateCardDialog
        open={pendingRetry !== null}
        onClose={() => setPendingRetry(null)}
        darkMode={darkMode}
        description={t(
          'manager.billing.changePlan.updateCardThenRetry',
          'Votre carte actuelle ne peut pas être prélevée. Mettez-la à jour pour finaliser votre abonnement.',
        )}
        onSuccess={async () => {
          const action = pendingRetry
          setPendingRetry(null)
          if (action === 'create') {
            await tryCreateSubscription()
          } else if (action === 'apply') {
            await tryApplyChange()
          }
        }}
      />
    </Dialog>
  )
}

// =============================================================================
// Bloc preview détaillé (lignes + total + récurrent)
// Le PreviewBlock n'est rendu que quand requires_reactivation === false, donc
// les champs lines / recurring sont garantis présents par le backend.
// =============================================================================

/** « 25 octobre » / "October 25" : the date of the first invoice. */
function dateFacture(iso: string | null | undefined, langue: string): string {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString(langue.startsWith('fr') ? 'fr-CA' : 'en-CA', {
    month: 'long',
    day: 'numeric',
  })
}

function PreviewBlock({
  preview,
  darkMode,
}: {
  preview: PreviewChangePlanResponse
  darkMode: boolean
}) {
  const { t, i18n } = useTranslation()
  const lines = preview.lines ?? []
  const recurring = preview.recurring
  const hasImmediate =
    !!preview.is_upgrade && Number(preview.total_immediate_cad ?? '0') > 0

  return (
    <div className="space-y-4">
      {hasImmediate && lines.length > 0 && (
        <div className="space-y-2">
          <p
            className={cn(
              'text-xs font-semibold uppercase tracking-wide',
              darkMode ? 'text-slate-400' : 'text-slate-500',
            )}
          >
            {t(
              'manager.billing.changePlan.immediateDetail',
              'Détail du prélèvement immédiat',
            )}
          </p>
          <div
            className={cn(
              'rounded-lg border divide-y text-sm',
              darkMode
                ? 'border-slate-700 divide-slate-700 bg-slate-800/50'
                : 'border-slate-200 divide-y-slate-100 bg-slate-50',
            )}
          >
            {lines.map((line, idx) => (
              <div
                key={idx}
                className="flex items-baseline justify-between gap-3 px-3 py-2"
              >
                <span
                  className={cn(
                    'text-sm',
                    darkMode ? 'text-slate-300' : 'text-slate-700',
                  )}
                >
                  {line.description}
                </span>
                <span
                  className={cn(
                    'text-sm font-medium tabular-nums whitespace-nowrap',
                    line.kind === 'credit'
                      ? darkMode
                        ? 'text-emerald-400'
                        : 'text-emerald-700'
                      : darkMode
                        ? 'text-white'
                        : 'text-slate-900',
                  )}
                >
                  {line.kind === 'credit' ? '−' : ''}
                  {formatCad(line.amount_cad.replace('-', ''))}
                </span>
              </div>
            ))}
            <div
              className={cn(
                'flex items-baseline justify-between gap-3 px-3 py-2.5',
                darkMode ? 'bg-slate-800' : 'bg-white',
              )}
            >
              <span
                className={cn(
                  'text-sm font-semibold',
                  darkMode ? 'text-white' : 'text-slate-900',
                )}
              >
                {t(
                  'manager.billing.changePlan.totalImmediate',
                  'À prélever maintenant (TTC)',
                )}
              </span>
              <span
                className={cn(
                  'text-base font-bold tabular-nums',
                  darkMode ? 'text-white' : 'text-slate-900',
                )}
              >
                {formatCad(preview.total_immediate_cad)}
              </span>
            </div>
          </div>
        </div>
      )}

      {/* Récurrent (toujours affiché) */}
      <div className="space-y-1">
        <p
          className={cn(
            'text-xs font-semibold uppercase tracking-wide',
            darkMode ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          {preview.en_essai
            ? t(
                'manager.billing.changePlan.recurringTrial',
                'Première facture, le {{date}}',
                { date: dateFacture(preview.premiere_facture_le, i18n.language) },
              )
            : hasImmediate
            ? t(
                'manager.billing.changePlan.recurringNext',
                'À partir du prochain cycle',
              )
            : t(
                'manager.billing.changePlan.recurringFrom',
                'Nouvelle facturation (effet au prochain renouvellement)',
              )}
        </p>
        <div
          className={cn(
            'rounded-lg border px-3 py-2.5 flex items-baseline justify-between gap-3',
            darkMode
              ? 'border-slate-700 bg-slate-800/50'
              : 'border-slate-200 bg-slate-50',
          )}
        >
          <span
            className={cn(
              'text-sm',
              darkMode ? 'text-slate-300' : 'text-slate-700',
            )}
          >
            {preview.target_plan.name}
            {recurring!.quantity > 1 && (
              <span
                className={cn(
                  'text-xs ml-1',
                  darkMode ? 'text-slate-500' : 'text-slate-500',
                )}
              >
                × {recurring!.quantity}
              </span>
            )}
          </span>
          <span
            className={cn(
              'text-sm font-semibold tabular-nums whitespace-nowrap',
              darkMode ? 'text-white' : 'text-slate-900',
            )}
          >
            {formatCad(recurring!.amount_cad)}
            <span
              className={cn(
                'text-xs font-normal ml-1',
                darkMode ? 'text-slate-500' : 'text-slate-500',
              )}
            >
              /
              {recurring!.interval === 'year'
                ? t('manager.billing.perYearShort', 'an')
                : t('manager.billing.perMonthShort', 'mois')}
            </span>
          </span>
        </div>
      </div>
    </div>
  )
}
